// The store "Update available" prompt on the home dashboard, as one unit: its
// state, its three display modes, the resume-refresh listener it owns, and what
// happens when it is tapped or dismissed.
//
// This is the FORWARD-looking card — "a newer version is available in the store".
// It shares the dashboard slot with the backward-looking "What's new" changelog
// nudge and takes priority over it; that rule lives with every other
// notice-coexistence rule in cs-day-items-notice-stack.ts. What does NOT live
// there is what happens when this card is dismissed: the slot has to be handed
// back, and that is `onWhatsNewReclaimSlotAsync` below.
//
// Moved out of CsDayItemsComponent by #5236, unchanged. The state table itself is
// `decideUpdateCardState` in cs-day-items-update-card.ts, which is pure and
// separately tested.
//
// Angular-free on purpose — see cs-day-items-notice-host.ts for why. That
// constraint is why this file takes the install state as a plain function rather
// than importing the enum's owning service; see `InstallStateReader`.

import { TranslatedString } from 'models/translated-string';
import { MinimumVersionStatus, UpdateInstallState } from 'services/version-check.dispatch';
import type { VersionCheckResult } from 'services/version-check.dispatch';
import { decideUpdateCardState } from './cs-day-items-update-card';
import { DayItemsNoticeController, DayItemsNoticeHost, markNoticeHostForCheck } from './cs-day-items-notice-host';

/**
 * The version-check backend, narrowed to what this card asks of it.
 *
 * `installState` is a SIGNAL read, and it must stay one. The three display
 * methods below call it while the template is being evaluated, which is what
 * registers it as a dependency of the view — that is how the card re-renders as
 * the Android flexible flow moves Idle → Downloading → Ready. Caching the value
 * into a field during a refresh would break that silently.
 */
export interface UpdateVersionCheck {
  supportsUpdateChecking(): boolean;
  getStatusAsync(surface: 'startup' | 'resume'): Promise<VersionCheckResult>;
  isDismissedRecentlyAsync(version: string | null): Promise<boolean>;
  /** Read LIVE, never cached — see the note above. */
  installState(): UpdateInstallState;
  recordDismissalAsync(version: string): Promise<void>;
  startUpdateAsync(): Promise<void>;
  completeUpdateAsync(): Promise<void>;
  /**
   * How the running build compares with config/MinimumSupportedVersion (#6770).
   * Optional so a fake without it reads as NotSet, which is the behaviour before
   * #6770: the test runner does not type-check, so a required member would only
   * fail at run time in every test whose fake lacks it.
   */
  getMinimumVersionStatusAsync?(): Promise<MinimumVersionStatus>;
}

/** The wordings the card draws, supplied so this file needs no i18n import. */
export interface UpdateCardWordings {
  downloading(): TranslatedString;
  ready(): TranslatedString;
  available(): TranslatedString;
  restartAction(): TranslatedString;
  updateAction(): TranslatedString;
  /** The Idle-mode text below the minimum supported version (#6770). Absent → `available()`. */
  unsupported?(): TranslatedString;
}

export interface UpdateAvailableNoticeDeps {
  host: DayItemsNoticeHost;
  versionCheck: UpdateVersionCheck;
  wordings: UpdateCardWordings;
  /** Emits one of the app_update telemetry events for this card. */
  trackAppUpdateEvent(action: 'shown' | 'clicked' | 'dismissed', version: string, belowMinimum: boolean): void;
  /** Reports a caught exception the user is not shown. */
  trackAbnormalException(err: unknown): void;
  /**
   * Hands the dashboard slot back to the "What's new" nudge after this card is
   * dismissed. An explicit edge between two cards, wired by the component — it
   * was a direct field write before #5236.
   */
  onWhatsNewReclaimSlotAsync(): Promise<void>;
}

/** The store "Update available" prompt. */
export class CsUpdateAvailableNotice implements DayItemsNoticeController {
  /** Whether this notice has something to say. Read by `decideVisibleNotices`. */
  wantsToShow = false;
  /** The version the store is offering. Null when the card is hidden. */
  availableVersion: string | null = null;
  /**
   * The running build is below config/MinimumSupportedVersion (#6770), as of the
   * last refresh. While true the card has no X and says the version is no longer
   * supported.
   */
  belowMinimum = false;

  /**
   * In-memory throttle so navigating away from and back to the dashboard within a
   * session does not re-emit app_update.shown for the same version. Cleared
   * naturally on cold start or page reload, because the host component is then
   * re-created; deliberately NOT cleared on a resume — that is the whole point of
   * the throttle, and persisting it would make it effectively once-per-version
   * ever. PRD §C9.
   */
  private shownTelemetryFiredFor = new Set<string>();

  constructor(private deps: UpdateAvailableNoticeDeps) {}

  /**
   * Refreshes the card from the version-check backend.
   *
   * Called on view init (surface=startup) and when the app is brought back to the
   * foreground (surface=resume); the backend throttles both to one query per
   * session per surface, so calling this often is cheap. Settings uses a separate
   * path that bypasses the throttle.
   */
  async refreshAsync(surface: 'startup' | 'resume'): Promise<void> {
    if (!this.deps.versionCheck.supportsUpdateChecking()) return;
    const result = await this.deps.versionCheck.getStatusAsync(surface);
    if (this.deps.host.isDestroyed()) return;

    // the minimum-version read only matters when there is a card to show
    const minimumStatus = result.updateAvailable && this.deps.versionCheck.getMinimumVersionStatusAsync
      ? await this.deps.versionCheck.getMinimumVersionStatusAsync()
      : MinimumVersionStatus.NotSet;
    if (this.deps.host.isDestroyed()) return;
    const belowMinimum = minimumStatus === MinimumVersionStatus.BelowMinimum;

    // the dismissal check only matters when there is a version to ask about —
    // short-circuit when the backend says no update, so a localStorage read is not
    // burned on every refresh
    const isDismissed = result.updateAvailable
      ? await this.deps.versionCheck.isDismissedRecentlyAsync(result.availableVersion)
      : false;
    if (this.deps.host.isDestroyed()) return;

    // an in-progress flexible download or restart keeps the card visible even past
    // a prior dismissal, so the Restart action stays reachable
    const installInProgress = this.deps.versionCheck.installState() !== UpdateInstallState.Idle;
    const decision = decideUpdateCardState(
      result,
      isDismissed,
      this.wantsToShow,
      this.shownTelemetryFiredFor,
      installInProgress,
      belowMinimum,
    );

    this.wantsToShow = decision.show;
    this.belowMinimum = belowMinimum;
    this.availableVersion = decision.availableVersion;
    if (decision.fireShownTelemetry && decision.availableVersion) {
      this.shownTelemetryFiredFor.add(decision.availableVersion);
      this.deps.trackAppUpdateEvent('shown', decision.availableVersion, belowMinimum);
    }
    markNoticeHostForCheck(this.deps.host);
  }

  // The card has three modes, driven by the backend's live install state (only
  // Android ever leaves Idle — see design-docs/in-app-update-prompt.md
  // §installState). The three methods below are called FROM THE TEMPLATE, and
  // each reads the install-state signal there, which is what auto-tracks it for
  // zoneless change detection.

  /** Card body text for the current mode. */
  message(): TranslatedString {
    switch (this.deps.versionCheck.installState()) {
      case UpdateInstallState.Downloading:
        return this.deps.wordings.downloading();
      case UpdateInstallState.Ready:
        return this.deps.wordings.ready();
      default:
        if (this.belowMinimum && this.deps.wordings.unsupported) return this.deps.wordings.unsupported();
        return this.deps.wordings.available();
    }
  }

  /**
   * Action-button label for the current mode. Returns undefined while downloading
   * so the card renders no button — there is nothing to tap mid-download. The
   * dismiss "X" is hidden in that mode too; see `isDismissible`.
   */
  actionText(): TranslatedString | undefined {
    switch (this.deps.versionCheck.installState()) {
      case UpdateInstallState.Downloading:
        return undefined;
      case UpdateInstallState.Ready:
        return this.deps.wordings.restartAction();
      default:
        return this.deps.wordings.updateAction();
    }
  }

  /**
   * Whether the card shows its dismiss "X". Only in the available (Idle) mode:
   * once a flexible update is downloading or downloaded, dismissing would strand
   * the in-progress update with no in-app way to resume or restart it. The card
   * then persists until the update finishes, or the process restarts. Below the
   * minimum supported version there is no X in any mode (#6770).
   */
  isDismissible(): boolean {
    return this.deps.versionCheck.installState() === UpdateInstallState.Idle && !this.belowMinimum;
  }

  /**
   * Routes the action tap to the right handler for the current mode: Ready →
   * finish and restart; otherwise → start the update flow. The Downloading mode
   * has no button, so it never reaches here.
   */
  async onActionAsync(): Promise<void> {
    if (this.deps.versionCheck.installState() === UpdateInstallState.Ready) {
      await this.onRestartActionAsync();
    } else {
      await this.onUpdateActionAsync();
    }
  }

  /**
   * Restart tap (Ready mode): hands off to the backend, which fires the completed
   * telemetry and triggers the OS restart that finishes installing the download.
   */
  async onRestartActionAsync(): Promise<void> {
    try {
      await this.deps.versionCheck.completeUpdateAsync();
    } catch (err) {
      // surface the backend failure rather than leaving a dead Restart button —
      // per CLAUDE.md "unexpected conditions must surface"
      this.deps.trackAbnormalException(err);
    }
  }

  /**
   * Update tap: fires the clicked telemetry, then hands off to the platform
   * backend. On Android, Play's flexible flow takes over the experience (download
   * progress plus a restart prompt). On the web app, the service-worker round trip
   * reloads the page onto the new bundle.
   */
  async onUpdateActionAsync(): Promise<void> {
    if (this.availableVersion) {
      this.deps.trackAppUpdateEvent('clicked', this.availableVersion, this.belowMinimum);
    }
    try {
      await this.deps.versionCheck.startUpdateAsync();
    } catch (err) {
      // surface the backend failure so the on-call sees the gap between the
      // clicked telemetry and the completed telemetry, rather than reading it as
      // the user silently giving up. Per CLAUDE.md "unexpected conditions must
      // surface".
      this.deps.trackAbnormalException(err);
    }
  }

  /**
   * Dismiss: fires the dismissed telemetry, records the dismissal so the same
   * version stays hidden for 24 hours, hides the card, then hands the dashboard
   * slot back to the "What's new" nudge if one is waiting.
   */
  async onDismissedAsync(): Promise<void> {
    if (this.availableVersion) {
      this.deps.trackAppUpdateEvent('dismissed', this.availableVersion, this.belowMinimum);
      await this.deps.versionCheck.recordDismissalAsync(this.availableVersion);
      // bail if the view was destroyed during the await above — writing fields on
      // a destroyed instance is harmless, but keeping every post-await mutation
      // behind the guard is the codebase pattern
      if (this.deps.host.isDestroyed()) return;
    }
    this.wantsToShow = false;
    this.availableVersion = null;
    // hand the slot back — without this it stays empty even when a changelog nudge
    // is waiting, because the only other thing that sets that card up runs once
    // per dashboard load
    await this.deps.onWhatsNewReclaimSlotAsync();
    markNoticeHostForCheck(this.deps.host);
  }

  /**
   * Nothing to release here: the resume listener that drives `refreshAsync` is a
   * platform subscription the component owns, because detaching it has to happen
   * before the destroy guard flips.
   */
  dispose(): void {}
}
